import { isRecord } from "@/utils/unknown";

/**
 * Drag payload of a palette widget: what dropping onto a slot should create.
 * Layout payloads carry their grid shape ("1×2" = 1 row, 2 columns).
 */
export type CanvasWidgetDropPayload =
  | { kind: "layout"; rows: number; columns: number }
  | { kind: "view" }
  | { kind: "timeline" }
  | { kind: "tasks" }
  | { kind: "calendar" };

const CANVAS_WIDGET_MIME = "application/x-routine-canvas-widget";

export function writeWidgetDropPayload(
  dataTransfer: DataTransfer,
  payload: CanvasWidgetDropPayload
): void {
  dataTransfer.setData(CANVAS_WIDGET_MIME, JSON.stringify(payload));
  dataTransfer.effectAllowed = "copy";
}

export function hasWidgetDropPayload(
  dataTransfer: DataTransfer | null
): boolean {
  return !!dataTransfer?.types.includes(CANVAS_WIDGET_MIME);
}

export function readWidgetDropPayload(
  dataTransfer: DataTransfer | null
): CanvasWidgetDropPayload | null {
  const raw = dataTransfer?.getData(CANVAS_WIDGET_MIME);
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isRecord(parsed)) return null;
  if (parsed.kind === "view") return { kind: "view" };
  if (parsed.kind === "timeline") return { kind: "timeline" };
  if (parsed.kind === "tasks") return { kind: "tasks" };
  if (parsed.kind === "calendar") return { kind: "calendar" };
  if (
    parsed.kind === "layout" &&
    typeof parsed.rows === "number" &&
    Number.isInteger(parsed.rows) &&
    parsed.rows >= 1 &&
    typeof parsed.columns === "number" &&
    Number.isInteger(parsed.columns) &&
    parsed.columns >= 1
  ) {
    return { kind: "layout", rows: parsed.rows, columns: parsed.columns };
  }
  return null;
}
